import { Component } from "@theme/component";
import { onDocumentLoaded, changeMetaThemeColor, isTouchDevice, setHeaderMenuStyle } from "@theme/utilities";

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

const STICKY_ALWAYS = "always";
const SCROLL_DIRECTION_NONE = "none";
const HEADER_COMPONENT = "header-component";
const MINIMUM_REACHED_ATTRIBUTE = "minimum-reached";
const MENU_DESKTOP_BREAKPOINT_PX = 750;
const MENU_RESTORE_HYSTERESIS_PX = 48;

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ["headerDrawerContainer", "headerMenu", "headerRowTop"];

  /**
   * Viewport width at which the inline menu can next be retried.
   * @type {number | null}
   */
  #menuDrawerRestoreWidth = null;

  /**
   * Smallest viewport width where the overflow list reported that the minimum
   * number of inline menu items still fit.
   * @type {number | null}
   */
  #minimumMenuFitWidth = null;

  /**
   * Whether the inline menu is currently being restored and remeasured.
   */
  #isRestoringMenu = false;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry?.borderBoxSize[0]) {
      return;
    }

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty("--header-height", `${roundedHeaderHeight}px`);

    // Retry only at a width already known to fit (or after a bounded fallback
    // interval), rather than toggling menu/drawer on every growth pixel.
    if (this.#menuDrawerRestoreWidth && window.innerWidth >= this.#menuDrawerRestoreWidth) {
      this.#restoreMenuVisibility();
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) {
      return;
    }

    // When body uses width: 100vw, it extends beyond the viewport by the scrollbar width.
    // This makes the header wider than the IntersectionObserver root bounds, so threshold: 1
    // never fires isIntersecting: true. Expand the root margin to compensate.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const config = {
      threshold: alwaysSticky ? 1 : 0,
      rootMargin: scrollbarWidth > 0 && alwaysSticky ? `0px ${scrollbarWidth}px 0px 0px` : "0px",
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) {
        return;
      }

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? "inactive" : "active";
        if (this.dataset.themeColor) {
          changeMetaThemeColor(this.dataset.themeColor);
        }
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === "active";
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = event => {
    const { minimumReached } = event.detail;

    if (!minimumReached) {
      this.#minimumMenuFitWidth =
        this.#minimumMenuFitWidth === null ? window.innerWidth : Math.min(this.#minimumMenuFitWidth, window.innerWidth);
      this.#isRestoringMenu = false;
      this.#updateMenuVisibility(false);
      return;
    }

    const restoreFailed = this.#isRestoringMenu;
    if (restoreFailed || (this.#minimumMenuFitWidth !== null && window.innerWidth >= this.#minimumMenuFitWidth)) {
      this.#minimumMenuFitWidth = null;
    }

    const nextRetryWidth = window.innerWidth + MENU_RESTORE_HYSTERESIS_PX;
    const firstDesktopRetryWidth = MENU_DESKTOP_BREAKPOINT_PX + MENU_RESTORE_HYSTERESIS_PX;
    const knownFitWidth =
      this.#minimumMenuFitWidth !== null && this.#minimumMenuFitWidth > window.innerWidth
        ? this.#minimumMenuFitWidth
        : null;
    const restoreWidth = restoreFailed
      ? Math.max(knownFitWidth ?? 0, nextRetryWidth)
      : (knownFitWidth ?? Math.max(firstDesktopRetryWidth, nextRetryWidth));

    this.#isRestoringMenu = false;
    this.#updateMenuVisibility(true, restoreWidth);
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) {
      return;
    }

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute("sticky");
    if (!this.#offscreen && stickyMode !== STICKY_ALWAYS) {
      return;
    }

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === STICKY_ALWAYS) {
      if (isAtTop) {
        this.dataset.scrollDirection = SCROLL_DIRECTION_NONE;
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = "up";
      } else {
        this.dataset.scrollDirection = "down";
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = "inactive";
        this.dataset.scrollDirection = SCROLL_DIRECTION_NONE;
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = "active";
        this.dataset.scrollDirection = "up";
      }
    } else {
      this.dataset.scrollDirection = SCROLL_DIRECTION_NONE;
      this.dataset.stickyState = "idle";
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   * @param {number} [restoreWidth] - Viewport width at which to retry the inline menu
   */
  #updateMenuVisibility(hideMenu, restoreWidth = window.innerWidth) {
    this.#menuDrawerRestoreWidth = hideMenu ? restoreWidth : null;
    setHeaderMenuStyle();
  }

  /**
   * Restores the inline menu after the viewport grows beyond the width where it
   * collapsed. The hidden overflow list cannot clear its own stale minimum state,
   * so release that state before showing the menu and remeasure in the same frame.
   * Running after setHeaderMenuStyle's queued write lets layout be measured while
   * still preventing the unclassified full menu from reaching a paint.
   */
  #restoreMenuVisibility() {
    const overflowList = this.refs.headerMenu.querySelector("overflow-list");

    this.#isRestoringMenu = true;
    overflowList?.removeAttribute(MINIMUM_REACHED_ATTRIBUTE);
    this.#updateMenuVisibility(false);

    requestAnimationFrame(() => {
      if (!overflowList?.isConnected) {
        this.#isRestoringMenu = false;
        return;
      }

      overflowList.dispatchEvent(new CustomEvent("reflow", { detail: {} }));

      // A fully fitting list has no overflow and therefore emits no
      // overflowMinimum=false event. Record that silent success here.
      if (!overflowList.hasAttribute(MINIMUM_REACHED_ATTRIBUTE)) {
        this.#isRestoringMenu = false;
        this.#minimumMenuFitWidth =
          this.#minimumMenuFitWidth === null
            ? window.innerWidth
            : Math.min(this.#minimumMenuFitWidth, window.innerWidth);
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();

    // A same-width navigation restores the overflow classification before this
    // module upgrades the new header. In that path no overflowMinimum event is
    // replayed, so seed the same bounded desktop retry that the event would have
    // established on the source page.
    const overflowList = this.refs.headerMenu.querySelector("overflow-list");
    if (!isTouchDevice() && overflowList?.hasAttribute(MINIMUM_REACHED_ATTRIBUTE)) {
      const restoreWidth = Math.max(
        MENU_DESKTOP_BREAKPOINT_PX + MENU_RESTORE_HYSTERESIS_PX,
        window.innerWidth + MENU_RESTORE_HYSTERESIS_PX,
      );
      this.#updateMenuVisibility(true, restoreWidth);
    }

    this.#resizeObserver.observe(this);
    this.addEventListener("overflowMinimum", /** @type {EventListener} */ (this.#handleOverflowMinimum));

    const stickyMode = this.getAttribute("sticky");
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === STICKY_ALWAYS);

      if (stickyMode === "scroll-up" || stickyMode === STICKY_ALWAYS) {
        document.addEventListener("scroll", this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.removeEventListener("overflowMinimum", /** @type {EventListener} */ (this.#handleOverflowMinimum));
    document.removeEventListener("scroll", this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }
    document.body.style.setProperty("--header-height", "0px");
  }
}

if (!customElements.get(HEADER_COMPONENT)) {
  customElements.define(HEADER_COMPONENT, HeaderComponent);
}

/**
 * Keeps the global body `--header-group-height` custom property equal to the summed, per-element
 * ROUNDED heights of the #header-group's children — EXCLUDING the transient view-transition spacer
 * (assets/view-transitions.js), whose height must never contribute (it would inflate the property and
 * shrink the homepage hero that binds `--hero-height-offset` to it). Re-observes children and prunes
 * detached entries on childList changes.
 *
 * Exported (and invoked once on document load below) so the otherwise load-bound logic can be driven
 * against a test-mounted DOM.
 * @param {ParentNode} [root=document] - Scope to find <header-component> and #header-group within.
 * @returns {() => void} Cleanup that disconnects both observers.
 */
export function initHeaderGroupHeight(root = document) {
  const header = root.querySelector(HEADER_COMPONENT);
  const headerGroup = root.querySelector("#header-group");

  // Note: Initial header heights are set via inline script in theme.liquid; this handles dynamic
  // updates after page load. With no header group there is nothing to track.
  if (!headerGroup) {
    return () => {
      // No header group on this page — nothing was observed, so there is nothing to disconnect.
    };
  }

  /** @type {Map<Element, number>} */
  const observedHeights = new Map();

  const recompute = () => {
    let headerGroupHeight = 0;
    for (const height of observedHeights.values()) {
      headerGroupHeight += height;
    }
    document.body.style.setProperty("--header-group-height", `${headerGroupHeight}px`);
  };

  const resizeObserver = new ResizeObserver(entries => {
    for (const entry of entries) {
      if (entry.target !== header || (header.hasAttribute("transparent") && header.parentElement?.nextElementSibling)) {
        // Round per-element to match the inline script in theme.liquid which sums
        // integer offsetHeight values. Rounding the aggregate instead of per-element
        // can differ by 1px when children have fractional heights, causing layout shift.
        observedHeights.set(entry.target, Math.round(entry.borderBoxSize[0]?.blockSize ?? 0));
      }
    }
    recompute();
  });

  if (header instanceof HTMLElement) {
    resizeObserver.observe(header);
  }

  // Skip elements explicitly marked as transient by other code (e.g. the view-transition spacer in
  // assets/view-transitions.js). Their height must not contribute to --header-group-height — see the
  // comment on the pruning pass below for the failure mode.
  /**
   * @param {Element} element
   */
  const shouldObserve = element => element instanceof HTMLElement && element.dataset.viewTransitionSpacer !== "true";

  // Observe all children of the header group
  for (const element of headerGroup.children) {
    if (shouldObserve(element)) {
      resizeObserver.observe(element);
    }
  }

  // Also observe the header group itself for child changes
  const mutationObserver = new MutationObserver(mutations => {
    let childListChanged = false;
    for (const mutation of mutations) {
      if (mutation.type === "childList") {
        childListChanged = true;
        // Re-observe all children when the list changes
        for (const element of headerGroup.children) {
          if (shouldObserve(element)) {
            resizeObserver.observe(element);
          }
        }
      }
    }
    // Defense in depth: prune entries for elements no longer in the header group. ResizeObserver
    // doesn't fire when an observed element is detached, so without this the Map could retain stale
    // heights — e.g. the view-transition spacer added in assets/view-transitions.js (sized to
    // .header-section's height at snapshot time) would leave a ghost entry that gets resummed on the
    // next resize, inflating --header-group-height and shrinking the homepage hero (which binds
    // --hero-height-offset to it). The skip filter above is the primary protection; this pruning
    // catches anything that slips past.
    if (childListChanged) {
      let pruned = false;
      for (const target of observedHeights.keys()) {
        if (target !== header && !headerGroup.contains(target)) {
          observedHeights.delete(target);
          resizeObserver.unobserve(target);
          pruned = true;
        }
      }
      if (pruned) {
        recompute();
      }
    }
  });

  mutationObserver.observe(headerGroup, { childList: true });

  return () => {
    resizeObserver.disconnect();
    mutationObserver.disconnect();
  };
}

onDocumentLoaded(() => {
  initHeaderGroupHeight();
});
